/**
 * Basemap styles for the geographic maps — the Alert Board's Map View and the Track page's
 * route map — and the operator's choice between them.
 *
 *  · `map`       — the site's own map. Resolved in this order:
 *                    1. OFFLINE VECTOR TILES installed on the server this page came from
 *                       (`/map-tiles/`, see build-script/map-tiles.sh). Works with no internet
 *                       at all — the reason this exists: police sites sit on SWAN, not the
 *                       internet.
 *                    2. The site's own raster tile server, when `GeoMapConfig.tile_url` was
 *                       changed away from the public OpenStreetMap default.
 *                    3. OpenFreeMap — a free, keyless public VECTOR map. Vector, because a
 *                       vector map is drawn by the GPU and zooms continuously instead of
 *                       swapping image grids, which is what makes the map feel smooth.
 *  · `satellite` — aerial imagery with road and place names drawn over it.
 *  · `terrain`   — contours and hill shading.
 *
 * Satellite and terrain come off the PUBLIC internet only. On an isolated node they are
 * unreachable, and the map says so by name, which is the cue to switch back to `map`.
 *
 * The choice is per BROWSER, not per deployment: it is how one operator likes to look at the
 * map, not a fact about the site. One key for both maps, so a desk that works on imagery gets
 * imagery on the Track page as well.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

export type BasemapId = 'map' | 'satellite' | 'terrain';

/** What `build-script/map-tiles.sh` writes next to the tile archive. */
export interface MapTilesManifest {
  /** File name of the archive inside `/map-tiles/`. */
  pmtiles: string;
  /** Human name of the area it covers — "Gujarat". */
  region?: string;
  /** [west, south, east, north] of the area the archive covers. */
  bbox?: [number, number, number, number];
  maxzoom?: number;
  /** Protomaps colour scheme the sprites were downloaded for. */
  flavor?: 'light' | 'dark' | 'white' | 'grayscale' | 'black';
}

interface SourceBase {
  /** What the switcher and the "unavailable" notice call it. */
  name: string;
  /** Heading and body of the notice shown when its tiles cannot be reached. */
  unavailableTitle: string;
  unavailableText: string;
}

export type BasemapSource =
  | (SourceBase & {
      kind: 'raster';
      /** XYZ template; `{s}` is spread over a/b/c, `{r}` is dropped. */
      url: string;
      attribution: string;
      /** Deepest zoom the source really has. Past it, tiles are upscaled rather than missing. */
      maxNativeZoom: number;
      /** Drawn over the base — the road and place names on satellite imagery. */
      overlays?: Array<{ url: string; attribution?: string }>;
    })
  | (SourceBase & {
      kind: 'style';
      /** A complete MapLibre style document, fetched by the map. */
      styleUrl: string;
    })
  | (SourceBase & {
      kind: 'pmtiles';
      /** Absolute URL of the `/map-tiles/` folder, with a trailing slash. */
      baseUrl: string;
      manifest: MapTilesManifest;
    });

export const BASEMAP_IDS: BasemapId[] = ['map', 'satellite', 'terrain'];

export const BASEMAP_LABELS: Record<BasemapId, string> = {
  map: 'Map',
  satellite: 'Satellite',
  terrain: 'Terrain',
};

const ESRI = 'https://server.arcgisonline.com/ArcGIS/rest/services';

function publicUnavailable(name: string): Pick<SourceBase, 'unavailableTitle' | 'unavailableText'> {
  return {
    unavailableTitle: `${name} view unavailable`,
    unavailableText:
      `${name} tiles come from a public internet service this browser cannot reach. Camera ` +
      'positions and their alerts are still shown. Switch back to Map to use the site’s own map.',
  };
}

const SATELLITE: BasemapSource = {
  kind: 'raster',
  name: 'Satellite',
  url: `${ESRI}/World_Imagery/MapServer/tile/{z}/{y}/{x}`,
  attribution:
    'Imagery &copy; <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics, and the GIS User Community',
  // Imagery in rural districts often stops at 18; past it Esri serves a "not available"
  // placeholder tile, which is worse than a slightly soft upscale of the real one.
  maxNativeZoom: 18,
  overlays: [
    { url: `${ESRI}/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}` },
    { url: `${ESRI}/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}` },
  ],
  ...publicUnavailable('Satellite'),
};

const TERRAIN: BasemapSource = {
  kind: 'raster',
  name: 'Terrain',
  url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
  attribution:
    'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, SRTM &middot; Style &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
  maxNativeZoom: 17,
  ...publicUnavailable('Terrain'),
};

/** The free public vector map used when the server has no offline tiles of its own. */
const ONLINE_VECTOR_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

/** The node's factory default — a site that never changed it has no tile server of its own. */
function isPublicOsm(url: string): boolean {
  return /(^|\/\/|\.)tile\.openstreetmap\.org\//.test(url);
}

// ── Offline tiles on this server ────────────────────────────────────────────

const MAP_TILES_PATH = '/map-tiles/';
const DETECT_TIMEOUT_MS = 4000;
let localTiles: Promise<MapTilesManifest | null> | null = null;

/**
 * Does the server this page came from carry offline map tiles?
 *
 * Asked once per page load. The node and the cloud each answer for themselves — the same
 * path, served by whichever nginx this browser is talking to — so the check is the same
 * code in both apps.
 */
function detectLocalTiles(): Promise<MapTilesManifest | null> {
  if (localTiles) return localTiles;
  localTiles = (async () => {
    if (typeof window === 'undefined') return null;
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), DETECT_TIMEOUT_MS);
    try {
      const res = await fetch(`${MAP_TILES_PATH}manifest.json`, { cache: 'no-cache', signal: ctl.signal });
      // The SPA fallback answers an unknown path with index.html and a 200 — only real JSON
      // naming an archive counts as "installed".
      if (!res.ok || !(res.headers.get('content-type') || '').includes('json')) return null;
      const m = (await res.json()) as MapTilesManifest;
      return m && typeof m.pmtiles === 'string' && m.pmtiles ? m : null;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  })();
  return localTiles;
}

function siteSource(
  configured: { tile_url: string; tile_attribution: string } | null,
  local: MapTilesManifest | null,
): BasemapSource | null {
  if (local) {
    return {
      kind: 'pmtiles',
      name: 'Map',
      baseUrl: new URL(MAP_TILES_PATH, window.location.origin).href,
      manifest: local,
      unavailableTitle: 'Basemap unavailable',
      unavailableText:
        'The offline map installed on this server could not be read. Camera positions and their alerts are still shown.',
    };
  }
  if (!configured) return null;
  if (configured.tile_url && !isPublicOsm(configured.tile_url)) {
    return {
      kind: 'raster',
      name: 'Map',
      url: configured.tile_url,
      attribution: configured.tile_attribution ?? '',
      maxNativeZoom: 19,
      unavailableTitle: 'Basemap unavailable',
      unavailableText:
        'Map tiles could not be reached — this browser has no route to the site’s tile server. ' +
        'Camera positions and their alerts are still shown.',
    };
  }
  return {
    kind: 'style',
    name: 'Map',
    styleUrl: ONLINE_VECTOR_STYLE,
    unavailableTitle: 'Basemap unavailable',
    unavailableText:
      'This server has no offline map installed and the online map cannot be reached. Camera ' +
      'positions and their alerts are still shown. Install the offline map on this server to ' +
      'get the basemap back without internet.',
  };
}

// ── The operator's choice ──────────────────────────────────────────────────

const STORAGE_KEY = 'trinetra.map.basemap';

function readChoice(): BasemapId {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && (BASEMAP_IDS as string[]).includes(v)) return v as BasemapId;
  } catch {
    /* private mode — the site's map is the answer */
  }
  return 'map';
}

/**
 * The basemap to draw, given the site's configured one.
 *
 * `source` is null while that is still being worked out — the config is loading, or the
 * server is being asked whether it carries offline tiles. The map draws its pins meanwhile.
 */
export function useBasemap(configured: { tile_url: string; tile_attribution: string } | null) {
  const [id, setIdState] = useState<BasemapId>(readChoice);
  // undefined = still asking the server; null = it has none.
  const [local, setLocal] = useState<MapTilesManifest | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    detectLocalTiles().then((m) => {
      if (live) setLocal(m);
    });
    return () => {
      live = false;
    };
  }, []);

  const setId = useCallback((next: BasemapId) => {
    setIdState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode — the choice lasts for this visit */
    }
  }, []);

  const tileUrl = configured?.tile_url;
  const tileAttribution = configured?.tile_attribution;
  const source = useMemo<BasemapSource | null>(() => {
    if (id === 'satellite') return SATELLITE;
    if (id === 'terrain') return TERRAIN;
    if (local === undefined) return null;
    return siteSource(tileUrl == null ? null : { tile_url: tileUrl, tile_attribution: tileAttribution ?? '' }, local);
  }, [id, local, tileUrl, tileAttribution]);

  return { id, setId, source };
}
